import React, { useState, useEffect } from 'react';
import { 
  Modal, FormControl, Box, Tooltip, Typography, TextField, 
  Button, Checkbox, FormControlLabel, FormGroup, IconButton, Snackbar,Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import { grey, red, blue } from '@mui/material/colors';

import BASE_URL from '../../config';

const style = {
  position: 'absolute', 
  top: '50%', 
  left: '50%', 
  transform: 'translate(-50%, -50%)',
  bgcolor: 'white', 
  borderRadius: 4, 
  p: 4, 
  outline: 'none',

  maxHeight: '90vh', 
  width: { xs: '90%', sm: '500px' }, 
  display: 'flex',
  flexDirection: 'column'
};

const boxstyle = {
      mx: 0.3,
      width:'auto', 
      maxHeight: '120px', 
      overflowY: 'auto', 
      bgcolor: 'white',
      '&::-webkit-scrollbar': { height: '6px', width: '6px',display:'none' },
      '&::-webkit-scrollbar-thumb': { background: grey[300], borderRadius: '8px' },
      '&::-webkit-scrollbar-thumb:hover': { background: grey[500] },
    };

const Inputstyle = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#f5f5f5',
    borderRadius: '8px',
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: grey[400] },
    '&.Mui-focused fieldset': { borderColor: grey[600] },
    '&.Mui-error fieldset': { borderColor: red[700] },
  },
};

const listaPermisos = [
  'Gestionar Usuarios', 'Gestionar Roles', 'Gestionar Clientes', 'Gestionar reportes',
  'Gestionar equipos/soluciones', 'Gestionar Tareas','Gestionar Agenda','Ver Agenda', 'Ver Reportes', 'Configuración Sistema'
];

export default function Popeditrol({ rol, refresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: '', descripcion: '', permisos: [] });
  const [confirmType, setConfirmType] = useState(null); // 'save' o 'cancel'
  const [error, setError] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isNameFocused, setIsNameFocused] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (rol && open) {
      setForm({
        nombre: rol.nombre || '',
        descripcion: rol.descripcion || '',
        permisos: Array.isArray(rol.permisos) ? rol.permisos : []
      });
      setError(false);
      setConfirmType(null);
    }
  }, [rol, open]);

  const handleCheck = (p) => {
    const nuevos = form.permisos.includes(p)
      ? form.permisos.filter(item => item !== p)
      : [...form.permisos, p];
    setForm({ ...form, permisos: nuevos });
  };

  const validateAndOpenSave = () => {
    const permisosValidos = form.permisos.filter(p => listaPermisos.includes(p));
    const isNombreEmpty = !form.nombre.trim();
    const isDescEmpty = !form.descripcion.trim();
    const isPermisosEmpty = permisosValidos.length === 0;

    if (isNombreEmpty || isDescEmpty || isPermisosEmpty) {
      setError(true);
      return;
    } 
    setError(false);
    setConfirmType('save');
    
  };

  const handleUpdate = async () => {
    try {
      const res = await fetch(`${BASE_URL}/roles/${rol.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        setSnackbar({ open: true, message: 'Ajustes realizados con éxito', severity: 'success' });
        setOpen(false);
        refresh();
      }
    } catch (e) { console.error("Error al actualizar:", e); }
  };

  const ConfirmView = () => (
    <Box sx={style}>
      <Typography variant="h6">
        {confirmType === 'cancel' ? '¿Cancelar edición?' : '¿Guardar cambios?'}
      </Typography>
      <Typography variant="body1" color='textSecondary' sx={{ mt: 1, mb: 3 }}>
        {confirmType === 'cancel' 
          ? '¿Estás seguro que deseas cancelar? Los cambios realizados se perderán.' 
          : '¿Estás seguro que deseas aplicar los cambios a este rol?'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'end' }}>
        <Button 
          variant="contained" 
          sx={{ bgcolor: grey[200], color: 'black', textTransform: 'none', px: 3}} 
          onClick={() => setConfirmType(null)}
        >
          {confirmType === 'cancel' ? 'No, continuar' : 'No, revisar'}
        </Button>
        <Button 
          variant="contained" 
          sx={{ bgcolor: 'black', color: 'white', textTransform: 'none', px: 3}}
          onClick={confirmType === 'cancel' ? () => setOpen(false) : handleUpdate}
        >
          {confirmType === 'cancel' ? 'Sí, cancelar' : 'Sí, guardar'}
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <IconButton onClick={() => setOpen(true)} 
      sx={{'&:hover': {color:grey[100], bgcolor: blue[400] } }}>
        <EditIcon fontSize="small" />
      </IconButton>

      <Modal open={open} onClose={() => setConfirmType('cancel')}>
        <Box>
          {confirmType ? <ConfirmView /> : (
            <Box sx={style}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6">Editar Rol</Typography>
                <Typography variant="body2" color='textSecondary'>Actualiza la información y permisos del rol</Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* NOMBRE */}
                <FormControl fullWidth>
                  <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5 }}>Nombre del rol</Typography>
                  <Tooltip 
                    title="Debes completar este campo" 
                    open={(isNameFocused || error) && !form.nombre.trim()} 
                    placement="top-end" arrow
                    componentsProps={{ tooltip: { sx: { bgcolor: red[700] } }, arrow: { sx: { color: red[700] } } }}
                  >
                    <TextField 
                      fullWidth variant="outlined" sx={Inputstyle} 
                      error={error && !form.nombre.trim()}
                      value={form.nombre}
                      onFocus={() => setIsNameFocused(true)}
                      onBlur={() => setIsNameFocused(false)}
                      onChange={(e) => setForm({...form, nombre: e.target.value})} 
                    />
                  </Tooltip>
                </FormControl>

                {/* DESCRIPCIÓN */}
                <FormControl fullWidth>
                  <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5 }}>Descripción</Typography>
                  <Tooltip 
                    title="Debes completar este campo" 
                    open={(isFocused || error) && !form.descripcion.trim()} 
                    error={(isFocused || error) && !form.descripcion.trim()} 
                    placement="top-end" arrow
                    componentsProps={{ tooltip: { sx: { bgcolor: red[700] } }, arrow: { sx: { color: red[700] } } }}
                  >
                    <TextField 
                      fullWidth multiline rows={2} variant="outlined" sx={Inputstyle}
                      error={error && !form.descripcion.trim()}
                      value={form.descripcion}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setIsFocused(false)}
                      onChange={(e) => setForm({...form, descripcion: e.target.value})} 
                    />
                  </Tooltip>
                </FormControl>

                {/* PERMISOS */}
                <FormGroup sx={{mb:3}}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Permisos</Typography>
                  <Tooltip 
                    title="Debes seleccionar al menos un permiso" 
                    open={error && form.permisos.length === 0} 
                    placement="top-end" 
                    arrow 
                    componentsProps={{ tooltip: { sx: { bgcolor: red[700] } }, arrow: { sx: { color: red[700] } } }}
                  >
                    <Box sx={{  px:0,py:1,
                      border: `1px solid ${error && form.permisos.length === 0 ? red[700] : grey[300]}`, 
                      borderRadius: '8px'}}>
                        <Box sx={boxstyle}>
                          {listaPermisos.map((p) => (
                            <Box 
                              key={p} 
                              sx={{ 
                                ':hover ': { bgcolor: grey[100] } ,
                                display: 'flex', 
                                alignItems: 'center', 
                                height: '28px' }}>
                              <FormControlLabel
                                control={
                                <Checkbox 
                                  size="medium" 
                                  checked={form.permisos.includes(p)} 
                                  onChange={() => handleCheck(p)}
                                  sx={{ color: error && form.permisos.length === 0 ? red[700] : undefined }}
                                  />}
                                  label={<Typography variant="body1">{p}</Typography>}
                                  sx={{ margin: 0, width: '100%'}}
                              />
                            </Box>
                          ))}
                        </Box>
                    </Box>
                  </Tooltip>
                </FormGroup>

                {/* BOTONES ACCIÓN */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'end' }}>
                  <Button 
                    variant="contained" 
                    sx={{ bgcolor: grey[100], color: 'black', 
                      width: { xs: '40%', sm: '25%' },
                      textTransform: 'none', px: 3, '&:hover': { bgcolor: grey[200] } }}
                    onClick={() => setConfirmType('cancel')}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    variant="contained" 
                    sx={{ bgcolor: 'black', color: 'white', 
                      width: { xs: '55%', sm: '38%' },
                      textTransform: 'none', px: 3, '&:hover': { bgcolor: '#333' } }}
                    onClick={validateAndOpenSave}
                  >
                    Guardar Cambios
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>
      <Snackbar open={snackbar.open} autoHideDuration={2500} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: '10px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}